import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { useAuth } from '../../context/AuthContext';
import {
  HiCog6Tooth,
  HiGlobeAlt,
  HiBolt,
  HiComputerDesktop,
  HiPlay,
  HiTrash,
  HiArrowPath,
  HiClock,
  HiDocumentText,
  HiEye,
  HiEyeSlash,
} from 'react-icons/hi2';
import toast from 'react-hot-toast';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

const DevToolsPage = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('api');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API Tester
  const [apiUrl, setApiUrl] = useState('');
  const [apiMethod, setApiMethod] = useState('GET');
  const [apiHeaders, setApiHeaders] = useState('{}');
  const [apiBody, setApiBody] = useState('');
  const [apiLoading, setApiLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [apiHistory, setApiHistory] = useState([]);

  // Webhook Inspector
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookPayload, setWebhookPayload] = useState('{}');
  const [webhookSource, setWebhookSource] = useState('');
  const [webhookLoading, setWebhookLoading] = useState(false);

  // Environment
  const [envMasked, setEnvMasked] = useState(true);

  const userId = currentUser?.uid;

  const loadApiHistory = async () => {
    if (!userId) return;
    try {
      const ref = collection(firestore, 'devTools', userId, 'apiTests');
      const q = query(ref, orderBy('createdAt', 'desc'), limit(50));
      const snapshot = await getDocs(q);
      setApiHistory(
        snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.()?.toISOString?.() || d.data().createdAt,
        }))
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Could not load API history:', err);
      setApiHistory([]);
    }
  };

  const loadWebhookEvents = async () => {
    if (!userId) return;
    try {
      const ref = collection(firestore, 'devTools', userId, 'webhookEvents');
      const q = query(ref, orderBy('createdAt', 'desc'), limit(50));
      const snapshot = await getDocs(q);
      setWebhookEvents(
        snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.()?.toISOString?.() || d.data().createdAt,
        }))
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Could not load webhook events:', err);
      setWebhookEvents([]);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([loadApiHistory(), loadWebhookEvents()]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (userId) load();
  }, [userId]);

  const runApiTest = async () => {
    if (!apiUrl.trim()) {
      toast.error('Enter a URL');
      return;
    }
    setApiLoading(true);
    setApiResponse(null);
    let parsedHeaders = {};
    try {
      parsedHeaders = JSON.parse(apiHeaders || '{}');
    } catch {
      toast.error('Invalid JSON in headers');
      setApiLoading(false);
      return;
    }

    const startTime = Date.now();
    try {
      const opts = {
        method: apiMethod,
        headers: { 'Content-Type': 'application/json', ...parsedHeaders },
      };
      if (['POST', 'PUT', 'PATCH'].includes(apiMethod) && apiBody.trim()) {
        try {
          opts.body = JSON.stringify(JSON.parse(apiBody));
        } catch {
          opts.body = apiBody;
        }
      }
      const res = await fetch(apiUrl, opts);
      const duration = Date.now() - startTime;
      const contentType = res.headers.get('content-type') || '';
      let body = await res.text();
      if (contentType.includes('application/json')) {
        try {
          body = JSON.stringify(JSON.parse(body), null, 2);
        } catch {}
      }
      const result = {
        status: res.status,
        statusText: res.statusText,
        duration,
        headers: Object.fromEntries(res.headers.entries()),
        body,
      };
      setApiResponse(result);

      if (userId) {
        const ref = collection(firestore, 'devTools', userId, 'apiTests');
        await addDoc(ref, {
          url: apiUrl,
          method: apiMethod,
          status: res.status,
          duration,
          createdAt: serverTimestamp(),
          userId,
        });
        await loadApiHistory();
      }
      toast.success(`Request completed in ${duration}ms`);
    } catch (err) {
      const duration = Date.now() - startTime;
      setApiResponse({
        status: 0,
        statusText: 'Request Failed',
        duration,
        error: err.message,
      });
      if (userId) {
        const ref = collection(firestore, 'devTools', userId, 'apiTests');
        await addDoc(ref, {
          url: apiUrl,
          method: apiMethod,
          status: 0,
          duration,
          error: err.message,
          createdAt: serverTimestamp(),
          userId,
        });
        await loadApiHistory();
      }
      toast.error(err.message);
    } finally {
      setApiLoading(false);
    }
  };

  const simulateWebhook = async () => {
    if (!userId) return;
    setWebhookLoading(true);
    try {
      let payload = {};
      try {
        payload = JSON.parse(webhookPayload || '{}');
      } catch {
        toast.error('Invalid JSON in payload');
        setWebhookLoading(false);
        return;
      }
      const ref = collection(firestore, 'devTools', userId, 'webhookEvents');
      await addDoc(ref, {
        source: webhookSource || 'manual',
        payload,
        status: 'simulated',
        createdAt: serverTimestamp(),
        userId,
      });
      await loadWebhookEvents();
      toast.success('Webhook event recorded');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWebhookLoading(false);
    }
  };

  const deleteApiTest = async id => {
    if (!userId) return;
    try {
      await deleteDoc(doc(firestore, 'devTools', userId, 'apiTests', id));
      await loadApiHistory();
      toast.success('Removed from history');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteWebhookEvent = async id => {
    if (!userId) return;
    try {
      await deleteDoc(doc(firestore, 'devTools', userId, 'webhookEvents', id));
      await loadWebhookEvents();
      toast.success('Event removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const envConfig = {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_FIREBASE_API_KEY: envMasked
      ? '••••••••'
      : process.env.REACT_APP_FIREBASE_API_KEY || '(not set)',
    REACT_APP_FIREBASE_AUTH_DOMAIN: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '(not set)',
    REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID || '(not set)',
    REACT_APP_FIREBASE_STORAGE_BUCKET: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '(not set)',
    REACT_APP_FIREBASE_MESSAGING_SENDER_ID:
      process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '(not set)',
    REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID || '(not set)',
    REACT_APP_FIREBASE_MEASUREMENT_ID: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '(not set)',
    REACT_APP_FIREBASE_DATABASE_URL: process.env.REACT_APP_FIREBASE_DATABASE_URL || '(not set)',
  };

  const tabs = [
    { id: 'api', label: 'API Tester', icon: HiGlobeAlt },
    { id: 'webhook', label: 'Webhook Inspector', icon: HiBolt },
    { id: 'env', label: 'Environment', icon: HiComputerDesktop },
  ];

  if (loading) {
    return <LoadingState message="Loading developer tools..." />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-500/25">
            <HiCog6Tooth className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Developer Tools</h1>
            <p className="text-sm text-slate-400">
              API tester, webhook inspector, and environment viewer
            </p>
          </div>
        </div>
      </motion.header>

      {/* Tabs */}
      <div className="flex gap-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-all ${
              activeTab === tab.id
                ? 'border-blue-500 bg-blue-600 text-white'
                : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500 hover:bg-slate-700 hover:text-slate-200'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'api' && (
          <motion.section
            key="api"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6">
              <h2 className="mb-4 text-lg font-semibold text-white">API Request</h2>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <select
                    value={apiMethod}
                    onChange={e => setApiMethod(e.target.value)}
                    className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-medium text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {HTTP_METHODS.map(m => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <input
                    type="url"
                    value={apiUrl}
                    onChange={e => setApiUrl(e.target.value)}
                    placeholder="https://api.example.com/endpoint"
                    className="min-w-[280px] flex-1 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={runApiTest}
                    disabled={apiLoading}
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {apiLoading ? (
                      <HiArrowPath className="h-4 w-4 animate-spin" />
                    ) : (
                      <HiPlay className="h-4 w-4" />
                    )}
                    {apiLoading ? 'Sending...' : 'Send'}
                  </motion.button>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-400">
                      Headers (JSON)
                    </label>
                    <textarea
                      value={apiHeaders}
                      onChange={e => setApiHeaders(e.target.value)}
                      rows={4}
                      className="w-full rounded-lg border border-slate-600 bg-slate-800 font-mono text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      placeholder='{"Authorization": "Bearer token"}'
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-400">
                      Body (JSON)
                    </label>
                    <textarea
                      value={apiBody}
                      onChange={e => setApiBody(e.target.value)}
                      rows={4}
                      className="w-full rounded-lg border border-slate-600 bg-slate-800 font-mono text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      placeholder='{"key": "value"}'
                    />
                  </div>
                </div>
              </div>
            </div>

            {apiResponse && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6"
              >
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
                  Response
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                      apiResponse.status >= 200 && apiResponse.status < 300
                        ? 'bg-green-900/50 text-green-300'
                        : apiResponse.status >= 400
                          ? 'bg-red-900/50 text-red-300'
                          : 'bg-amber-900/50 text-amber-300'
                    }`}
                  >
                    {apiResponse.status} {apiResponse.statusText}
                  </span>
                  <span className="text-xs font-normal text-slate-400">
                    {apiResponse.duration}ms
                  </span>
                </h2>
                <pre className="max-h-64 overflow-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-200">
                  {apiResponse.error || apiResponse.body}
                </pre>
              </motion.div>
            )}

            <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
                <HiClock className="h-5 w-5" />
                API Test History
              </h2>
              {apiHistory.length === 0 ? (
                <p className="text-sm text-slate-400">
                  No API tests yet. Run a request to save history.
                </p>
              ) : (
                <div className="space-y-2">
                  {apiHistory.map(item => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between rounded-lg border border-slate-600 bg-slate-800/50 px-4 py-3"
                    >
                      <div className="flex items-center gap-4">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-bold ${
                            item.status >= 200 && item.status < 300
                              ? 'bg-green-900/50 text-green-300'
                              : item.status >= 400
                                ? 'bg-red-900/50 text-red-300'
                                : 'bg-slate-700 text-slate-300'
                          }`}
                        >
                          {item.status || 'ERR'}
                        </span>
                        <span className="font-mono text-xs">{item.method}</span>
                        <span className="max-w-[280px] truncate text-sm text-slate-300">
                          {item.url}
                        </span>
                        <span className="text-xs text-slate-400">{item.duration}ms</span>
                      </div>
                      <button
                        onClick={() => deleteApiTest(item.id)}
                        className="rounded p-1.5 text-slate-400 hover:bg-red-900/30 hover:text-red-400"
                        title="Remove"
                      >
                        <HiTrash className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.section>
        )}

        {activeTab === 'webhook' && (
          <motion.section
            key="webhook"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6">
              <h2 className="mb-4 text-lg font-semibold text-white">Simulate Webhook Event</h2>
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-400">Source</label>
                  <input
                    type="text"
                    value={webhookSource}
                    onChange={e => setWebhookSource(e.target.value)}
                    placeholder="e.g. stripe, github, custom"
                    className="w-full rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-400">
                    Payload (JSON)
                  </label>
                  <textarea
                    value={webhookPayload}
                    onChange={e => setWebhookPayload(e.target.value)}
                    rows={6}
                    className="w-full rounded-lg border border-slate-600 bg-slate-800 font-mono text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder='{"event": "payment.created", "data": {...}}'
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={simulateWebhook}
                  disabled={webhookLoading}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {webhookLoading ? (
                    <HiArrowPath className="h-4 w-4 animate-spin" />
                  ) : (
                    <HiBolt className="h-4 w-4" />
                  )}
                  Record Event
                </motion.button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
                <HiDocumentText className="h-5 w-5" />
                Webhook Events
              </h2>
              {webhookEvents.length === 0 ? (
                <p className="text-sm text-slate-500">No webhook events yet. Simulate one above.</p>
              ) : (
                <div className="space-y-3">
                  {webhookEvents.map(item => (
                    <div
                      key={item.id}
                      className="rounded-lg border border-slate-600 bg-slate-800/50 p-4"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300">
                              {item.source || 'unknown'}
                            </span>
                            <span className="text-xs text-slate-500">{item.createdAt}</span>
                          </div>
                          <pre className="mt-2 max-h-32 overflow-auto rounded bg-white p-2 text-xs dark:bg-slate-900">
                            {typeof item.payload === 'object'
                              ? JSON.stringify(item.payload, null, 2)
                              : item.payload}
                          </pre>
                        </div>
                        <button
                          onClick={() => deleteWebhookEvent(item.id)}
                          className="rounded p-1.5 text-slate-400 hover:bg-red-900/30 hover:text-red-400"
                          title="Remove"
                        >
                          <HiTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.section>
        )}

        {activeTab === 'env' && (
          <motion.section
            key="env"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Environment Configuration</h2>
                <button
                  onClick={() => setEnvMasked(!envMasked)}
                  className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700"
                >
                  {envMasked ? <HiEye className="h-4 w-4" /> : <HiEyeSlash className="h-4 w-4" />}
                  {envMasked ? 'Show API Key' : 'Hide API Key'}
                </button>
              </div>
              <p className="mb-4 text-sm text-slate-500">
                Runtime configuration for the current build. Sensitive values are masked by default.
              </p>
              <div className="space-y-2">
                {Object.entries(envConfig).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center gap-4 rounded-lg border border-slate-600 bg-slate-800/50 px-4 py-3"
                  >
                    <span className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                      {key}
                    </span>
                    <span className="flex-1 truncate text-right font-mono text-xs text-slate-600 dark:text-slate-400">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DevToolsPage;
